/**
 * Meters and musical time. All rhythm positions and durations are integer
 * ticks: 48 per quarter note, which divides evenly into eighths (24),
 * sixteenths (12), dotted values and (later) triplets (16, 8) — no rounding.
 */
export const TICKS_PER_QUARTER = 48;
export const TICKS_PER_WHOLE = TICKS_PER_QUARTER * 4;

export const TEMPO_MIN = 40;
export const TEMPO_MAX = 160;

export type MeterKind = 'simple' | 'compound';

export interface TimeSignature {
  id: string;
  numerator: number;
  denominator: number;
  kind: MeterKind;
  /** Ticks in one counted beat (the unit the tempo is measured in). */
  beatTicks: number;
  /** Beats per measure. */
  beats: number;
  measureTicks: number;
  /** What one beat is, for labels: "quarter note", "half note", "dotted quarter". */
  beatName: string;
  /**
   * How the beats group into strong and weaker pulses: beats per group, first
   * group starting on the downbeat. The first entry is the default; meters such
   * as 5/4 (3+2 or 2+3) allow several. The downbeat is strongest, the start of
   * every other group comes next ("medium"), and the remaining beats are weak.
   */
  groupings: number[][];
  /** Beam eighths and sixteenths in groups of this many ticks (one beat, or a quarter in 2/2). */
  beamTicks: number;
}

function meter(numerator: number, denominator: number, kind: MeterKind, groupings?: number[][]): TimeSignature {
  const measureTicks = (numerator * TICKS_PER_WHOLE) / denominator;
  const beatTicks = kind === 'compound' ? (3 * TICKS_PER_WHOLE) / denominator : TICKS_PER_WHOLE / denominator;
  const beatName = kind === 'compound' ? 'dotted quarter' : denominator === 2 ? 'half note' : 'quarter note';
  return {
    id: `${numerator}/${denominator}`,
    numerator,
    denominator,
    kind,
    beatTicks,
    beats: measureTicks / beatTicks,
    measureTicks,
    beatName,
    groupings: groupings ?? [[measureTicks / beatTicks]],
    beamTicks: Math.min(beatTicks, kind === 'compound' ? beatTicks : TICKS_PER_QUARTER),
  };
}

/**
 * Supported meters. 5/4, 6/4 and 7/4 are counted in quarter-note beats and
 * treated as simple meters; their beats group in more than one way (owner's
 * curriculum, 2026-10-05). 4/4 and 12/8 are strong–weak–medium–weak.
 */
export const TIME_SIGNATURES: readonly TimeSignature[] = [
  meter(2, 4, 'simple'),
  meter(3, 4, 'simple'),
  meter(4, 4, 'simple', [[2, 2]]),
  meter(5, 4, 'simple', [[3, 2], [2, 3]]),
  meter(6, 4, 'simple', [[3, 3], [2, 2, 2]]),
  meter(7, 4, 'simple', [[4, 3], [3, 4], [2, 3, 2]]),
  meter(2, 2, 'simple'),
  meter(6, 8, 'compound'),
  meter(9, 8, 'compound'),
  meter(12, 8, 'compound', [[2, 2]]),
];

export const TIME_SIGNATURE_BY_ID: ReadonlyMap<string, TimeSignature> = new Map(TIME_SIGNATURES.map((t) => [t.id, t]));

export function getTimeSignature(id: string): TimeSignature {
  const ts = TIME_SIGNATURE_BY_ID.get(id);
  if (!ts) throw new Error(`Unknown time signature ${id}`);
  return ts;
}

/** Milliseconds per tick at a tempo counted in the meter's beat. */
export function msPerTick(ts: TimeSignature, bpm: number): number {
  return 60000 / bpm / ts.beatTicks;
}

export function beatMs(bpm: number): number {
  return 60000 / bpm;
}

/** Strong (downbeat), medium (start of a later group), weak beat, and — when counting eighths in compound time — the eighths between beats. */
export type BeatAccent = 'downbeat' | 'secondary' | 'beat' | 'division';

/** The meter's default grouping, or `grouping` when it is one the meter allows. */
export function resolveGrouping(ts: TimeSignature, grouping?: readonly number[] | null): number[] {
  return grouping && isGroupingFor(ts, grouping) ? [...grouping] : [...ts.groupings[0]!];
}

export function isGroupingFor(ts: TimeSignature, grouping: readonly number[]): boolean {
  return ts.groupings.some((g) => g.length === grouping.length && g.every((n, i) => n === grouping[i]));
}

/** Beat indexes (0-based, within a measure) where each group starts. */
export function groupStartBeats(grouping: readonly number[]): number[] {
  const starts: number[] = [];
  let beat = 0;
  for (const size of grouping) {
    starts.push(beat);
    beat += size;
  }
  return starts;
}

/** "3+2" — only meaningful for meters with more than one group. */
export function groupingLabel(grouping: readonly number[]): string {
  return grouping.join('+');
}

export function beatAccent(ts: TimeSignature, beatIndex: number, grouping?: readonly number[] | null): BeatAccent {
  if (beatIndex === 0) return 'downbeat';
  return groupStartBeats(resolveGrouping(ts, grouping)).includes(beatIndex) ? 'secondary' : 'beat';
}
